import type { DriveStep } from 'driver.js';
import { filterExistingSteps } from './tourFilter';
import { withMobileSidebarOpen } from './tourMobileSidebar';
import { localizeSteps } from './tourI18n';

// Full catalog of reference-tour steps across the whole app. Steps whose
// `element` selector isn't present in the current page's DOM are dropped by
// getReferenceSteps() below — this is what makes the reference tour
// "page-aware" without any route-detection logic: it just reflects whatever
// happens to be on screen when the user clicks the Tutorial button.
//
// Steps targeting `#tour-nav-*` live inside the sidebar, which is an
// off-canvas drawer below SIDEBAR_DRAWER_QUERY (hooks/useIsMobile.ts, mirroring
// Layout.scss) — each is wrapped with withMobileSidebarOpen so the drawer opens
// for the step and closes again once the tour moves on, at those widths only.
const ALL_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'tutorials.welcomeRealityCoach',
      description: 'tutorials.quickReferenceTourTools',
      showButtons: ['next', 'close'],
    },
  },
  withMobileSidebarOpen({
    element: '#tour-nav-home',
    popover: {
      title: 'tutorials.home',
      description: 'tutorials.landingPageShowsWhat',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-roleplay',
    popover: {
      title: 'tutorials.rolePlay',
      description: 'tutorials.startNewRoleplaySession',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-feedback',
    popover: {
      title: 'tutorials.feedback',
      description: 'tutorials.reviewPastSessionsScores',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-users',
    popover: {
      title: 'tutorials.users',
      description: 'tutorials.manageUsersOrganization',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-join-requests',
    popover: {
      title: 'tutorials.joinRequests',
      description: 'tutorials.reviewPeopleAskingJoin',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-classes',
    popover: {
      title: 'tutorials.classes',
      description: 'tutorials.manageClassesRosters',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-dashboard',
    popover: {
      title: 'tutorials.dashboard',
      description: 'tutorials.viewAnalyticsReportingAcross',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-app-feedback',
    popover: {
      title: 'tutorials.appFeedback',
      description: 'tutorials.seeFeedbackUsersHave',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-organizations',
    popover: {
      title: 'tutorials.organizations',
      description: 'tutorials.manageOrganizationsTheirSub',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-organization',
    popover: {
      title: 'tutorials.myOrganization',
      description: 'tutorials.manageYourOrganizationDepartments',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-reports',
    popover: {
      title: 'tutorials.reports',
      description: 'tutorials.trackTrialSubscribedRenewal',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-characters',
    popover: {
      title: 'tutorials.characters',
      description: 'tutorials.manageAiStudentsUsed',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-scene-editor',
    popover: {
      title: 'tutorials.sceneEditor',
      description: 'tutorials.configure3dClassroomScenes',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-prompt-studio',
    popover: {
      title: 'tutorials.promptStudio',
      description: 'tutorials.authorEditAiPrompts',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-reviews',
    popover: {
      title: 'tutorials.reviews',
      description: 'tutorials.manageRolePlayReview',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-reviewer-dashboard',
    popover: {
      title: 'tutorials.myReviews',
      description: 'tutorials.reviewRoleplaySessionsAssigned',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-assignments',
    popover: {
      title: 'tutorials.myAssignments',
      description: 'tutorials.trackAssignedTrainingBundles',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-practice',
    popover: {
      title: 'tutorials.myPractice',
      description: 'tutorials.seeYourPractice',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-grades',
    popover: {
      title: 'tutorials.myGrades',
      description: 'tutorials.seeYourGrades',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-classes',
    popover: {
      title: 'tutorials.myClasses',
      description: 'tutorials.seeYourClasses',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-assignments',
    popover: {
      title: 'tutorials.assignments',
      description: 'tutorials.createManageTrainingAssignments',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-bundles',
    popover: {
      title: 'tutorials.bundles',
      description: 'tutorials.createManageContentBundles',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-progress',
    popover: {
      title: 'tutorials.myProgress',
      description: 'tutorials.trackXpStreaksBadges',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-team-progress',
    popover: {
      title: 'tutorials.teamProgress',
      description: 'tutorials.trackXpStreaksOrg',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-platform-dashboard',
    popover: {
      title: 'tutorials.platformOverview',
      description: 'tutorials.highLevelStatsShort',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-competitions',
    popover: {
      title: 'tutorials.competitions',
      description: 'tutorials.joinManageCompetitionsCheck',
    },
  }),
  {
    element: '#tour-header-help',
    popover: {
      title: 'common.sendFeedback',
      description: 'tutorials.haveSuggestionFoundBug',
    },
  },
  {
    element: '#tour-header-bell',
    popover: {
      title: 'tutorials.notifications',
      description: 'tutorials.checkUpdatesAlertsRelevant',
    },
  },
  {
    element: '#tour-header-user-menu',
    popover: {
      title: 'tutorials.userMenu',
      description: 'tutorials.accessProfileDeviceSetup',
    },
  },
  {
    element: '#tour-header-tutorial',
    popover: {
      title: 'tutorials.tutorial',
      description: 'tutorials.clickButtonAnytimeReplay',
    },
  },
  {
    element: '#tour-scenario-selector',
    popover: {
      title: 'tutorials.scenarioSelector',
      description: 'tutorials.chooseRoleplayScenarioBegin',
    },
  },
  {
    element: '#tour-classroom-scene',
    popover: {
      title: 'tutorials.classroomAvatars',
      description: 'tutorials.clickStudentAvatarClassroom',
    },
  },
  {
    element: '#tour-mic-button',
    popover: {
      title: 'tutorials.pushTalkMic',
      description: 'tutorials.clickMuteUnmuteMic',
    },
  },
  {
    element: '#tour-end-session',
    popover: {
      title: 'common.endSession',
      description: 'tutorials.clickWhenReadyWrapUp',
    },
  },
  {
    element: '#tour-sessions-list',
    popover: {
      title: 'tutorials.sessionHistory',
      description: 'tutorials.browsePastSessionsHere',
    },
  },
  {
    element: '#tour-sessions-search',
    popover: {
      title: 'tutorials.searchSessions',
      description: 'tutorials.searchSessionsIdNumber',
    },
  },
  {
    element: '#tour-session-kpis',
    popover: {
      title: 'tutorials.scoreKpis',
      description: 'tutorials.seeScoreStepsCompleted',
    },
  },
  {
    element: '#tour-session-feedback',
    popover: {
      title: 'tutorials.aiCoachingFeedback',
      description: 'tutorials.readAiGeneratedCoaching',
    },
  },
  {
    element: '#tour-session-steps',
    popover: {
      title: 'common.teachingSteps',
      description: 'tutorials.trackHowWellCompleted',
    },
  },
  {
    element: '#tour-session-line-feedback',
    popover: {
      title: 'tutorials.betterWaysToSayIt',
      description: 'tutorials.lineFeedbackYourSession',
    },
  },
  {
    element: '#tour-session-speech-metrics',
    popover: {
      title: 'tutorials.speechMetrics',
      description: 'tutorials.speechMetricsYourSession',
    },
  },
  {
    element: '#tour-session-recommendations',
    popover: {
      title: 'tutorials.recommendations',
      description: 'tutorials.recommendedResourcesYourSession',
    },
  },
  {
    element: '#tour-session-transcript',
    popover: {
      title: 'common.transcript',
      description: 'tutorials.reviewTranscriptThisSession',
    },
  },
  {
    element: '#tour-session-human-review',
    popover: {
      title: 'common.humanReview',
      description: 'tutorials.trainersAdminsCanAlso',
    },
  },
  {
    popover: {
      title: 'tutorials.thatsTheTour',
      description: 'tutorials.clickTutorialButtonHeader',
      showButtons: ['next', 'close'],
      doneBtnText: 'common.finish',
    },
  },
];

export function getReferenceSteps(): DriveStep[] {
  return localizeSteps(filterExistingSteps(ALL_STEPS));
}
